import { animate, state, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatTooltip } from '@angular/material/tooltip';
import { forkJoin, Observable, of } from 'rxjs';
import { debounceTime, startWith, switchMap, tap } from 'rxjs/operators';
import { DataRulesDataService } from '../../data-rules-data.service';
import { AllPrimaryEntitiesNames } from '../../data-rules-model';
import { MetaDataService, MetaSource } from '../../meta-data.service';
import { IOptionItem } from '../autocomplete/autocomplete.component';
import { IProvider, IRuleOptionsItem, RuleOptionsProvider, StaticOptionsProvider } from '../option-items-providers';

const transitionEntityInput = trigger(
  'showEntityInput', [
    state('opened', style({
      opacity: 1,
    })),
    state('closed', style({
      opacity: 0,
    })),
    transition('opened => closed', [
      animate('0.2s ease-in')
    ]),
    transition('closed => opened', [
      animate('0.7s ease-out')
    ]),
  ]
);

const transitionRuleInput = trigger(
  'showRuleInput', [
    state('opened', style({
      marginLeft: '10px',
      opacity: '1',
    })),
    state('closed', style({
      marginLeft: 'calc(100% - (37px + 10px))',
      opacity: 0,
    })),
    transition('opened => closed', [
      animate('0.3s ease-in')
    ]),
    transition('closed => opened', [
      animate('0.4s ease-out')
    ]),
  ]
);

@Component({
  selector: 'app-paired-rule-autocomplete',
  templateUrl: './paired-rule-autocomplete.component.html',
  styleUrls: ['./paired-rule-autocomplete.component.scss'],
  imports: [
    NgClass,
    NgIf,
    MatTooltip,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    NgForOf,
    AsyncPipe
  ],
  animations: [transitionEntityInput, transitionRuleInput]
})
export class PairedRuleAutocompleteComponent implements OnInit, AfterViewInit {
  @Input() inputRequired: boolean;
  @Input() selectedRuleControl: AbstractControl;
  @Input() disabledRulesFunc: () => Observable<string[]>;

  @Output() inputCleared = new EventEmitter();

  // core one property - many of the template states depends from this
  selectedEntity: AllPrimaryEntitiesNames;

  isSearching: boolean;
  isLoading: boolean;
  entitySelectorsMap: any;
  rulePlaceholder: string;

  entityOptionsControl: FormControl;
  entityOptionsProvider: IProvider<IOptionItem>;
  $filteredEntities: Observable<IOptionItem[]>;
  ruleOptionsControl: FormControl;
  ruleOptionsProvider: IProvider<IRuleOptionsItem>;
  $filteredRules: Observable<IRuleOptionsItem[]>;

  @ViewChild('entityInput') entityInput: ElementRef<HTMLElement>;
  @ViewChild('ruleInput') ruleInput: ElementRef<HTMLElement>;

  constructor(
    private metaDataService: MetaDataService, private dataRulesDataService: DataRulesDataService,
  ) {
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (
      event.code === 'Backspace'
      && this.selectedEntity
      && this.ruleOptionsControl.value.length === 0
    ) {
      this.clearInput(this.entityInput.nativeElement, true);
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.entityOptionsControl = new FormControl(
      {disabled: !this.selectedRuleControl || this.selectedRuleControl.disabled},
      this.inputRequired ? Validators.required : null
    );
    this.ruleOptionsControl = new FormControl({},
      this.inputRequired ? Validators.required : null
    );

    const meta$ = this.metaDataService.getAllMeta();
    const rules$ = this.dataRulesDataService.initialize(false);

    forkJoin({meta: meta$, rules: rules$})
      .subscribe(() => {
        const entityOptions = this.metaDataService.getEntitiesOptions({sourceFrom: MetaSource.DATA_SOURCE, primaryOnly: true});
        const optionsProvider = new StaticOptionsProvider(entityOptions);
        this.setEntityProvider(optionsProvider);

        this.entitySelectorsMap = this.metaDataService.getEntitiesSelectorsMap();

        this.isLoading = false;
        this.$filteredEntities = this.entityOptionsControl.valueChanges.pipe(
          debounceTime(400),
          startWith(''),
          tap(_ => this.isSearching = true),
          switchMap(value => this.entityOptionsProvider ? this.entityOptionsProvider.filter(value) : of([])),
          tap(_ => this.isSearching = false),
          tap(result => console.log('PairedRuleAutocomplete: entity options.valueChange results', result.length))
        );
        this.$filteredRules = this.ruleOptionsControl.valueChanges.pipe(
          debounceTime(400),
          startWith(''),
          tap(_ => this.isSearching = true),
          switchMap(value => this.ruleOptionsProvider ? this.ruleOptionsProvider.filter(value) : of([])),
          tap(_ => this.isSearching = false),
          tap(result => console.log('PairedRuleAutocomplete: rule options.valueChange results', result.length))
        );
      });
  }

  ngAfterViewInit() {
    this.entityInput.nativeElement.focus();
  }

  clearInput(inputElement: HTMLElement, focusInput: boolean): void {
    this.entityOptionsControl.setValue('');
    this.ruleOptionsControl.setValue('');
    if (focusInput)
      inputElement.focus();
    this.selectedEntity = null;
    this.selectedRuleControl.setValue(0);
    this.inputCleared.emit();
  }

  private setEntityProvider(optionsProvider: IProvider<IOptionItem>, selOption?: any): void {
    this.entityOptionsProvider = optionsProvider;
    this.entityOptionsControl.setValue(selOption ? selOption : '');   // trigger a value change causing the dropdown to be populated
  }

  private setRuleProvider(optionsProvider: IProvider<IRuleOptionsItem>, selOption?: any): void {
    this.ruleOptionsProvider = optionsProvider;
    this.ruleOptionsControl.setValue(selOption ? selOption : '');   // trigger a value change causing the dropdown to be populated
  }

  onEntitySelected(e: MatAutocompleteSelectedEvent): void {
    const entity: AllPrimaryEntitiesNames = e.option.value;
    this.isLoading = true;

    // apply new ruleProvider
    forkJoin({
      usedRules: this.disabledRulesFunc(),
      ruleOptions: of(this.dataRulesDataService.getRuleOptionsForEntity(entity))
    }).subscribe(({usedRules, ruleOptions}) => {

      const optionsProvider = new RuleOptionsProvider(
        ruleOptions.map(ruleOption => {
          const isRuleUsed = usedRules.findIndex(ur => ur === ruleOption.value) > -1;
          ruleOption.used = isRuleUsed;
          return ruleOption;
        })
      );
      this.setRuleProvider(optionsProvider);

      if (optionsProvider.optionsLength === 0) {
        this.rulePlaceholder = `No rules on ${entity}`;
      } else {
        this.rulePlaceholder = 'Enter a rule';
      }
      this.isLoading = false;
      this.selectedEntity = entity;
      this.ruleInput.nativeElement.focus();
    });
  }

  onRuleSelected(e: MatAutocompleteSelectedEvent): void {
    const ruleId: string = e.option.value;

    // might be some other rule control output actions
    this.selectedRuleControl.setValue(ruleId);
  }

  entityOptionDisplayValue(name: any): string {
    const option = this.entityOptionsProvider ? this.entityOptionsProvider.get(name) : null;
    return option ? option.label : '';
  }

  ruleOptionDisplayValue(name: any): string {
    const option = this.ruleOptionsProvider ? this.ruleOptionsProvider.get(name) : null;
    return option ? option.label : '';
  }

  renderIcon(entity: string): string {
    const defaultNames = () => {
      if (entity.includes('candidate'))
        return 'bhi-candidate';
      if (entity.includes('contact') || entity.includes('person'))
        return 'bhi-person';
      if (entity.includes('client-corporation'))
        return 'bhi-company';
      if (entity.includes('submission'))
        return 'bhi-job submission';
      if (entity.includes('job'))
        return 'bhi-job';
      if (entity.includes('lead'))
        return 'bhi-lead';
      if (entity.includes('opportunity'))
        return 'bhi-opportunity';
      if (entity.includes('placement'))
        return 'bhi-star';
      if (entity.includes('appointment'))
        return 'bhi-appointment';
      if (entity.includes('task'))
        return 'bhi-check-o';
      if (entity.includes('note'))
        return 'bhi-add-note';
      return 'bhi-puzzle';
    };
    const defaultName = defaultNames();
    return entity.includes('custom-object')
    // todo hardcoded
    || entity === 'person'
      ? `${defaultName} custom-object` : defaultName;
  }
}
